import mapboxgl from 'mapbox-gl';
import { toast } from 'react-hot-toast';

export class MapRouting {
  constructor(map) {
    this.map = map;
    this.currentRoute = null;
    this.currentMarker = null;
    this.routeSource = null;
    this.routeLayer = null;
    this.route = null;
    this.transportMode = 'driving'; // Mode par défaut
    this.userLocation = null;
    this.currentPopup = null;  // Ajouter cette ligne
    this.initializeRouteSource();
  }

  initializeRouteSource() {
    if (!this.map.getSource('route')) {
      this.map.addSource('route', {
        'type': 'geojson',
        'data': {
          'type': 'Feature',
          'properties': {},
          'geometry': {
            'type': 'LineString',
            'coordinates': []
          }
        }
      });

      this.map.addLayer({
        'id': 'route',
        'type': 'line',
        'source': 'route',
        'layout': {
          'line-join': 'round',
          'line-cap': 'round'
        },
        'paint': {
          'line-color': '#3b82f6',
          'line-width': 4,
          'line-opacity': 0.75
        }
      });
    }
  }

  // Méthode pour le bouton "Itinéraire" dans le popup
  async initializeRoute(coordinates) {
    if (!coordinates) {
      console.error('No coordinates provided');
      return;
    }

    try {
      // Obtenir la position de l'utilisateur
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      const start = [position.coords.longitude, position.coords.latitude];
      
      // Animation de la carte vers le point de départ
      this.map.flyTo({
        center: start,
        zoom: 14,
        duration: 2000,
        essential: true
      });

      // Calculer l'itinéraire avec les estimations
      this.calculateDistance(
        start,
        coordinates,
        (routeInfo) => {
          if (routeInfo) {
            console.log('Route calculated successfully');
          }
        }
      );
    } catch (error) {
      console.error('Error initializing route:', error);
      toast.error('Veuillez activer la géolocalisation pour voir l\'itinéraire');
    }
  }

  // Méthode pour calculer la distance entre deux points
  async calculateDistance(start, end, callback) {
    try {
      console.log('MapRouting - Calculating distance between:', start, end);
      
      if (!start || !end || !Array.isArray(start) || !Array.isArray(end)) {
        console.error('Invalid coordinates:', { start, end });
        return;
      }

      this.clearRoute();

      const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${start[0]},${start[1]};${end[0]},${end[1]}?geometries=geojson&access_token=${mapboxgl.accessToken}`;

      fetch(url)
        .then(response => response.json())
        .then(data => {
          if (!data.routes || data.routes.length === 0) {
            throw new Error('Aucun itinéraire trouvé');
          }

          const route = data.routes[0];
          const distance = (route.distance / 1000).toFixed(2); // Distance en km
          const duration = Math.round(route.duration / 60); // Durée en minutes

          // Mettre à jour la source de l'itinéraire
          this.map.getSource('route').setData({
            'type': 'Feature',
            'properties': {},
            'geometry': route.geometry
          });

          // Ajuster la vue pour voir tout l'itinéraire
          const coordinates = route.geometry.coordinates;
          const bounds = coordinates.reduce((bounds, coord) => {
            return bounds.extend(coord);
          }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

          this.map.fitBounds(bounds, {
            padding: 50,
            duration: 1000
          });

          // Calcul des temps pour différents modes de transport
          const speedModes = {
            driving: 50,
            cycling: 15,
            walking: 5
          };

          const times = {};
          for (const [mode, speed] of Object.entries(speedModes)) {
            const timeInHours = distance / speed;
            const hours = Math.floor(timeInHours);
            const minutes = Math.round((timeInHours - hours) * 60);
            times[mode] = hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
          }

          callback({
            distance: distance,
            duration: duration,
            driving: {
              distance: distance,
              duration: times.driving
            },
            cycling: {
              distance: distance,
              duration: times.cycling
            },
            walking: {
              distance: distance,
              duration: times.walking
            }
          });
        })
        .catch(error => {
          console.error('Erreur lors du calcul de l\'itinéraire:', error);
          callback(null);
        });
    } catch (error) {
      console.error('Error in calculateDistance:', error);
    }
  }

  // Nouvelle méthode pour afficher les informations de l'itinéraire
  showRouteInfo(position, routeInfo) {
    if (this.currentPopup) {
      this.currentPopup.remove();
    }

    const isMobile = window.innerWidth <= 768;
    const popupOffset = isMobile ? [0, -15] : [0, -10];
    const maxWidth = isMobile ? '90vw' : '300px';

    const popupContent = `
      <div class="p-4 bg-white rounded-lg shadow-lg" style="width: ${isMobile ? '100%' : 'auto'}">
        <div class="flex justify-between items-start mb-3">
          <h3 class="text-lg font-semibold text-gray-800">Estimation du trajet</h3>
          <button 
            onclick="window.routingInstance.closePopup()"
            class="text-gray-400 hover:text-gray-600"
            aria-label="Fermer"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p class="text-sm text-gray-600 mb-3">
          <span class="font-medium">Distance:</span> ${routeInfo.driving.distance} km
        </p>
        <div class="space-y-3">
          <div class="flex items-center gap-2">
            <span>🚗</span>
            <p class="text-sm text-gray-600">
              <span class="font-medium">En voiture:</span> 
              ${routeInfo.driving.duration}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <span>🚲</span>
            <p class="text-sm text-gray-600">
              <span class="font-medium">À vélo:</span> 
              ${routeInfo.cycling.duration}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <span>🚶</span>
            <p class="text-sm text-gray-600">
              <span class="font-medium">À pied:</span> 
              ${routeInfo.walking.duration}
            </p>
          </div>
        </div>
        <div class="flex gap-2 mt-4">
          <button 
            onclick="window.routingInstance.cancelRoute()"
            class="flex-1 px-3 py-1.5 bg-red-50 text-red-600 rounded text-sm hover:bg-red-100 transition-colors"
          >
            Annuler
          </button>
          <button 
            onclick="window.routingInstance.closePopup()"
            class="flex-1 px-3 py-1.5 bg-gray-50 text-gray-600 rounded text-sm hover:bg-gray-100 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    `;

    this.currentPopup = new mapboxgl.Popup({
      closeButton: false,
      className: 'route-popup',
      maxWidth: maxWidth,
      offset: popupOffset,
      anchor: isMobile ? 'bottom' : 'top'
    })
      .setLngLat(position)
      .setHTML(popupContent)
      .addTo(this.map);

    // Ajuster la vue sur mobile
    if (isMobile) {
      this.map.easeTo({
        center: position,
        padding: { top: 50, bottom: 200, left: 20, right: 20 },
        duration: 1000
      });
    }
  }

  // Nouvelle méthode pour fermer uniquement le popup
  closePopup() {
    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }
  }

  // Nouvelle méthode pour annuler complètement l'itinéraire
  cancelRoute() {
    // Supprimer le tracé de l'itinéraire
    if (this.map.getLayer('route')) {
      this.map.removeLayer('route');
    }
    if (this.map.getSource('route')) {
      this.map.removeSource('route');
    }

    // Supprimer les marqueurs
    if (this.startMarker) {
      this.startMarker.remove();
      this.startMarker = null;
    }
    if (this.endMarker) {
      this.endMarker.remove();
      this.endMarker = null;
    }

    // Supprimer le popup
    this.closePopup();

    // Réinitialiser les autres propriétés
    this.routeLayer = false;
    this.routeSource = false;
    this.currentRoute = null;
  }

  // Méthode pour calculer l'itinéraire (utilisée par le bouton "Itinéraire")
  calculateRoute(start, end) {
    if (!start || !end) return;

    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${start[0]},${start[1]};${end[0]},${end[1]}?steps=true&geometries=geojson&access_token=${mapboxgl.accessToken}`;

    fetch(url)
      .then(response => response.json())
      .then(data => {
        if (data.routes && data.routes.length > 0) {
          const route = data.routes[0];
          this.drawRoute(route.geometry);
        }
      })
      .catch(error => {
        console.error('Error calculating route:', error);
      });
  }

  // Méthode pour dessiner la route sur la carte
  drawRoute(geometry) {
    this.clearRoute();

    this.map.addSource('route', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: geometry
      }
    });

    this.map.addLayer({
      id: 'route',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#3887be',
        'line-width': 5,
        'line-opacity': 0.75
      }
    });

    // Ajuster la vue pour montrer toute la route
    const coordinates = geometry.coordinates;
    const bounds = coordinates.reduce((bounds, coord) => {
      return bounds.extend(coord);
    }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

    this.map.fitBounds(bounds, {
      padding: 50,
      duration: 1000
    });

    this.currentRoute = true;
  }

  getCurrentPosition() {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      });
    });
  }

  displayRoute(coordinates) {
    if (!this.map.getSource('route')) {
      this.map.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: coordinates
          }
        }
      });

      this.map.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#3b82f6',
          'line-width': 4,
          'line-opacity': 0.75
        }
      });
    } else {
      this.map.getSource('route').setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coordinates
        }
      });
    }
  }

  displayMarker(coordinates) {
    if (this.currentMarker) {
      this.currentMarker.remove();
    }

    this.currentMarker = new mapboxgl.Marker()
      .setLngLat(coordinates)
      .addTo(this.map);
  }

  fitRouteBounds(coordinates) {
    const bounds = coordinates.reduce((bounds, coord) => {
      return bounds.extend(coord);
    }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

    this.map.fitBounds(bounds, {
      padding: 50,
      duration: 1000
    });
  }

  clearRoute() {
    if (this.routeLayer && this.map.getLayer('route')) {
      this.map.removeLayer('route');
      this.routeLayer = false;
    }
    if (this.routeSource && this.map.getSource('route')) {
      this.map.removeSource('route');
      this.routeSource = false;
    }

    if (this.currentMarker) {
      this.currentMarker.remove();
      this.currentMarker = null;
    }

    if (this.startMarker) {
      this.startMarker.remove();
      this.startMarker = null;
    }
    if (this.endMarker) {
      this.endMarker.remove();
      this.endMarker = null;
    }

    // Supprimer également tous les popups
    const popups = document.querySelectorAll('.mapboxgl-popup');
    popups.forEach(popup => popup.remove());
  }

  clearRouteAndPopup() {
    this.clearRoute();
    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }
  }

  addRouteMarkers(start, end) {
    if (this.startMarker) this.startMarker.remove();
    if (this.endMarker) this.endMarker.remove();

    this.startMarker = new mapboxgl.Marker({ color: '#3b82f6' })
      .setLngLat(start)
      .addTo(this.map);

    this.endMarker = new mapboxgl.Marker({ color: '#ef4444' })
      .setLngLat(end)
      .addTo(this.map);
  }
}